import Parse from 'parse/dist/parse.min.js';
import { generateMagicLinkToken, checkMagicLinkStatus } from '../features/auth/services/tokenService';

// User services
export const findUserByEmail = async (email) => {
  try {
    const query = new Parse.Query(Parse.User);
    query.equalTo('username', email); 
    return await query.first();
  } catch (error) {
    console.error('Error finding user:', error);
    throw error;
  }
};

// Quotation services
export const createQuotation = async (quotationData) => {
  try {
    const Quotation = Parse.Object.extend('Quotation');
    const quotation = new Quotation();

    // Generar magic link único
    const magicLink = generateMagicLink();

    // Convertir los items a un formato que pueda ser guardado
    // Importante: Capturar todos los datos relevantes del producto
    const items = quotationData.items.map(item => {
      console.log('Guardando item en cotización:', item);
      return {
        id: item.id,
        name: item.name || item.title || 'No especificado', // Asegurar que siempre hay un nombre
        categoryName: item.categoryName,
        bulkQuantity: item.bulkQuantity,
        code: item.code || item.sku || 'Sin SKU', // Asegurar que siempre hay un código
        countPerBottle: item.countPerBottle || [], // Cantidad de pastillas por frasco
        imageUrl: item.image?.url || null,
        description: item.description || '',
        details: JSON.stringify(item)
      };
    });

    quotation.set('userEmail', quotationData.email);
    quotation.set('userName', quotationData.name);
    quotation.set('userPhone', quotationData.phone);
    quotation.set('items', items);
    quotation.set('status', 'pending');
    quotation.set('magicLink', magicLink);
    quotation.set('converted', false);

    // Campos opcionales
    if (quotationData.company) {
      quotation.set('userCompany', quotationData.company);
    }
    if (quotationData.message) {
      quotation.set('message', quotationData.message);
    }
    if (quotationData.additionalNotes) {
      quotation.set('additionalNotes', quotationData.additionalNotes);
    }

    await quotation.save();
    return quotation;
  } catch (error) {
    console.error('Error creating quotation:', error);
    throw error;
  }
};

export const getQuotationByMagicLink = async (magicLink) => {
  try {
    // Verificar si el magic link ha expirado
    const magicLinkStatus = checkMagicLinkStatus(magicLink);
    if (magicLinkStatus.isExpired) {
      console.warn('Magic link expirado:', magicLink);
      throw new Error('El enlace ha expirado. Por favor, solicite un nuevo enlace.');
    }
    
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    query.equalTo('magicLink', magicLink);
    query.include('user'); // Include the user object
    const quotation = await query.first();
    
    if (!quotation) {
      throw new Error('Cotización no encontrada');
    }
    
    // Añadir información de expiración al objeto de cotización
    quotation.set('_expirationInfo', {
      isExpired: magicLinkStatus.isExpired,
      expiresAt: new Date(magicLinkStatus.expiresAt),
      remainingTime: magicLinkStatus.remainingTime
    }, { silent: true }); // No dispara eventos de cambio
    
    return quotation;
  } catch (error) {
    console.error('Error getting quotation by magic link:', error);
    throw error;
  }
};

export const updateQuotationStatus = async (quotationId, status) => {
  try {
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    const quotation = await query.get(quotationId);
    
    quotation.set('status', status);
    await quotation.save();
    return quotation;
  } catch (error) {
    console.error('Error updating quotation status:', error);
    throw error;
  }
};

export const getUserQuotations = async (user) => {
  try {
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    query.equalTo('user', user);
    query.descending('createdAt');
    return await query.find();
  } catch (error) {
    console.error('Error getting user quotations:', error);
    throw error;
  }
};

// Utility function to generate a unique magic link
const generateMagicLink = () => {
  // Crear un objeto con datos mínimos para el token
  const mockUserData = {
    id: Date.now().toString(),
    get: (field) => {
      switch(field) {
        case 'email': return 'temp@example.com';
        case 'userType': return 'client';
        default: return null;
      }
    }
  };
  
  // Usar el nuevo servicio de tokens para generar un magic link con expiración
  return generateMagicLinkToken(mockUserData);
};

// Función para obtener todas las cotizaciones para el dashboard de administración
export const getAllQuotations = async () => {
  try {
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    query.descending('createdAt');
    query.include('user');
    return await query.find();
  } catch (error) {
    console.error('Error getting all quotations:', error);
    throw error;
  }
};

// Función para obtener estadísticas de cotizaciones y métricas para el dashboard
export const getQuotationStats = async () => {
  try {
    // Importar el servicio de usuarios
    const { getUserStats } = await import('./userService');
    
    // Obtener todas las cotizaciones
    const Quotation = Parse.Object.extend('Quotation');
    const quotationQuery = new Parse.Query(Quotation);
    quotationQuery.include('user');
    quotationQuery.limit(1000); // Asegurar que obtenemos todas las cotizaciones
    const allQuotations = await quotationQuery.find();
    
    // Obtener estadísticas de usuarios
    const userStats = await getUserStats();
    
    // Estadísticas por estado
    const statsByStatus = {
      total: allQuotations.length,
      pending: 0,
      estimado_listo: 0,
      completada: 0,
      rechazada: 0
    };
    
    // Estadísticas por mes (últimos 6 meses)
    const today = new Date();
    const monthStats = {};
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    
    // Inicializar los últimos 6 meses
    for (let i = 0; i < 6; i++) {
      const month = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthKey = `${month.getFullYear()}-${month.getMonth() + 1}`;
      const displayName = `${monthNames[month.getMonth()]} ${month.getFullYear()}`;
      monthStats[monthKey] = {
        count: 0,
        displayName: displayName,
        revenue: 0
      };
    }
    
    // Preparar estructura para clientes con cotizaciones
    const clientsWithQuotations = new Set();
    
    // Estadísticas de productos
    const productStats = {
      topRequested: {},
      totalValue: 0
    };
    
    // Calcular estadísticas
    allQuotations.forEach(quotation => {
      // Por estado
      const status = quotation.get('status');
      if (status && statsByStatus.hasOwnProperty(status)) {
        statsByStatus[status]++;
      }
      
      // Por mes
      const createdAt = quotation.get('createdAt');
      if (createdAt) {
        const monthKey = `${createdAt.getFullYear()}-${createdAt.getMonth() + 1}`;
        if (monthStats.hasOwnProperty(monthKey)) {
          monthStats[monthKey].count++;
          
          // Calcular valor estimado si está disponible
          const estimatedValue = quotation.get('estimatedValue') || 0;
          monthStats[monthKey].revenue += estimatedValue;
        }
        
        // Verificar si es un usuario nuevo este mes
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        if (createdAt.getMonth() === currentMonth && createdAt.getFullYear() === currentYear) {
          userStats.newThisMonth++;
        }
      }
      
      // Estadísticas de clientes
      const userEmail = quotation.get('userEmail');
      if (userEmail) {
        clientsWithQuotations.add(userEmail);
      }
      
      // Estadísticas de productos solicitados
      const items = quotation.get('items') || [];
      items.forEach(item => {
        if (item.name) {
          productStats.topRequested[item.name] = (productStats.topRequested[item.name] || 0) + 1;
          productStats.totalValue += (item.price || 0) * (item.quantity || 1);
        }
      });
    });
    
    // Añadir el número de clientes con cotizaciones a las estadísticas de usuarios
    userStats.clientsWithQuotations = clientsWithQuotations.size;
    
    // Ordenar productos más solicitados
    const topProducts = Object.entries(productStats.topRequested)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
    
    return {
      byStatus: statsByStatus,
      byMonth: monthStats,
      users: userStats,
      products: {
        ...productStats,
        topRequested: topProducts
      },
      conversionRate: (statsByStatus.completada / statsByStatus.total * 100).toFixed(1)
    };
  } catch (error) {
    console.error('Error getting quotation stats:', error);
    throw error;
  }
};
